/**
 * Me / Profile screen — Tank Redesign port (PR 3 of Tank Redesign).
 *
 * No exact mockup section in ~/Downloads/tank-redesign/ — the redesign's
 * editorial-dark visual language (Eyebrow + Card sections, mono labels,
 * phosphor accent) is applied to the existing content structure.
 *
 * Preserves all existing data: profile fields, language preference (with
 * server sync via updateMyLanguage), auto-enter-crop preference, role,
 * permissions, camera serials, user ID, service-account flag.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Card, Eyebrow, Mono } from '../shared/components/redesign';
import { updateMyLanguage, updateMyPreferences, fetchSessions } from '../api';
import { useUIStore } from '../stores/useUIStore';
import { useLocalStorage, useLocalStorageBool } from '../hooks/useLocalStorage';
import { STORAGE_KEYS } from '../utils/constants';
import styles from './Me.module.css';

const ROLE_CLASS = {
  admin: styles.roleAdmin,
  editor: styles.roleEditor,
  viewer: styles.roleViewer,
  photographer: styles.rolePhotographer,
};

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'nl', label: 'Nederlands' },
  { value: 'fr', label: 'Francais' },
];

function Field({ label, children }) {
  return (
    <div className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      <span className={styles.fieldValue}>{children}</span>
    </div>
  );
}

function SegmentedControl({ options, value, onChange }) {
  return (
    <div className={styles.segmented} role="tablist">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="tab"
          aria-selected={value === option.value}
          className={`${styles.segment} ${value === option.value ? styles.segmentActive : ''}`}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

// Local-only preference storage. Backend persistence to follow once
// the operator exposes a per-user preferences blob.
function useLocalPref(key, defaultValue) {
  const [value, setValue] = useState(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw == null ? defaultValue : JSON.parse(raw);
    } catch {
      return defaultValue;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* ignore quota errors */
    }
  }, [key, value]);
  return [value, setValue];
}

function PrefRow({ label, children }) {
  return (
    <div className={styles.prefRow}>
      <span className={styles.prefRowLabel}>{label}</span>
      <span className={styles.prefRowControl}>{children}</span>
    </div>
  );
}

export default function Me({ user }) {
  const { t, i18n } = useTranslation();

  const autoEnterCropMode = useUIStore((state) => state.autoEnterCropMode);
  const setAutoEnterCropMode = useUIStore((state) => state.setAutoEnterCropMode);
  const showVizImage = useUIStore((state) => state.showVizImage);
  const setShowVizImage = useUIStore((state) => state.setShowVizImage);
  const showCropMask = useUIStore((state) => state.showCropMask);
  const setShowCropMask = useUIStore((state) => state.setShowCropMask);

  // Display + Editor preferences. Grid layout + hide-spectators are
  // localStorage-backed at the page level; viz/crop-mask are in useUIStore
  // (already persisted via partialize).
  const [gridLayout, setGridLayout] = useLocalStorage('matrix-grid-layout', 'masonry');
  const [hideSpectators, setHideSpectators] = useLocalStorageBool(
    STORAGE_KEYS.HIDE_SPECTATORS,
    false,
  );
  const [defaultMode, setDefaultMode] = useLocalPref('me.defaultMode', 'crop');
  const [autoAdvance, setAutoAdvance] = useLocalPref('me.autoAdvance', true);
  const [showShortcuts, setShowShortcuts] = useLocalPref('me.showShortcuts', true);
  const [density, setDensity] = useLocalPref('me.density', 'standard');

  // Server-backed: auto-remove out-of-crop tagged faces. Lives on
  // user.preferences.auto_remove_mode so the backend can apply it on
  // every crop publish without the client having to re-send the value.
  const [autoRemoveMode, setAutoRemoveModeState] = useState(
    () => user?.preferences?.auto_remove_mode || 'off'
  );
  useEffect(() => {
    setAutoRemoveModeState(user?.preferences?.auto_remove_mode || 'off');
  }, [user?.preferences?.auto_remove_mode]);
  const handleAutoRemoveModeChange = async (mode) => {
    setAutoRemoveModeState(mode);
    try {
      await updateMyPreferences({ auto_remove_mode: mode });
    } catch (e) {
      console.warn('Failed to save auto_remove_mode:', e);
    }
  };

  const [sessions, setSessions] = useState([]);
  const [sessionsError, setSessionsError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchSessions()
      .then((data) => {
        if (!cancelled) setSessions(data?.sessions || []);
      })
      .catch((e) => {
        if (!cancelled) setSessionsError(e?.message || 'Failed to load sessions');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!user) {
    return (
      <div className={styles.screen}>
        <div className={styles.loading}>{t('me.loading')}</div>
      </div>
    );
  }

  const handleLanguageChange = async (lang) => {
    i18n.changeLanguage(lang);
    try {
      await updateMyLanguage(lang);
    } catch (e) {
      console.warn('Failed to save language preference:', e);
    }
  };

  const roleLabel = t(`roles.${user.role}`, { defaultValue: user.role });

  return (
    <div className={styles.screen}>
      <div className={styles.column}>
        {/* Route + section name come from TankShell's TopBar; we don't
            duplicate them here. */}

        <Card>
          <Eyebrow className={styles.sectionEyebrow}>{t('me.profile')}</Eyebrow>
          <div className={styles.fieldGrid}>
            <Field label={t('me.username')}>
              <Mono>{user.username}</Mono>
            </Field>
            <Field label={t('me.email')}>{user.email || '—'}</Field>
            <Field label={t('me.role')}>
              <span className={`${styles.roleBadge} ${ROLE_CLASS[user.role] || ''}`}>
                {roleLabel}
              </span>
            </Field>
            {user.company && (
              <Field label={t('me.company')}>{user.company.name}</Field>
            )}
          </div>
        </Card>

        <Card>
          <Eyebrow className={styles.sectionEyebrow}>{t('me.preferences')}</Eyebrow>
          <div className={styles.section}>
            <div className={styles.preference}>
              <label className={styles.checkboxRow}>
                <input
                  type="checkbox"
                  className={styles.checkbox}
                  checked={autoEnterCropMode}
                  onChange={(e) => setAutoEnterCropMode(e.target.checked)}
                />
                {t('me.autoEnterCrop')}
              </label>
              <p className={styles.hint}>{t('me.autoEnterCropHint')}</p>
            </div>
            <div className={styles.preference}>
              <span className={styles.preferenceLabel}>{t('me.language')}</span>
              <SegmentedControl
                options={LANGUAGE_OPTIONS}
                value={i18n.language}
                onChange={handleLanguageChange}
              />
              <p className={styles.hint}>{t('me.languageHint')}</p>
            </div>
            <div className={styles.preference}>
              <span className={styles.preferenceLabel}>Grid layout</span>
              <SegmentedControl
                options={[
                  { value: 'masonry', label: 'Masonry' },
                  { value: 'fit', label: 'Fit (4:3)' },
                ]}
                value={gridLayout}
                onChange={setGridLayout}
              />
              <p className={styles.hint}>
                Default Overview tile layout. The header toggle still overrides for the current session.
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <Eyebrow className={styles.sectionEyebrow}>Editor preferences</Eyebrow>
          <div className={styles.prefList}>
            <PrefRow label="Default mode on open">
              <SegmentedControl
                options={[
                  { value: 'crop', label: 'Crop' },
                  { value: 'view', label: 'View' },
                ]}
                value={defaultMode}
                onChange={setDefaultMode}
              />
            </PrefRow>
            <PrefRow label="Auto-advance after Done">
              <SegmentedControl
                options={[
                  { value: true, label: 'On' },
                  { value: false, label: 'Off' },
                ]}
                value={autoAdvance}
                onChange={setAutoAdvance}
              />
            </PrefRow>
            <PrefRow label="Show shortcuts inline">
              <SegmentedControl
                options={[
                  { value: true, label: 'On' },
                  { value: false, label: 'Off' },
                ]}
                value={showShortcuts}
                onChange={setShowShortcuts}
              />
            </PrefRow>
            <PrefRow label="Density">
              <SegmentedControl
                options={[
                  { value: 'compact', label: 'Compact' },
                  { value: 'standard', label: 'Standard' },
                  { value: 'comfortable', label: 'Comfortable' },
                ]}
                value={density}
                onChange={setDensity}
              />
            </PrefRow>
            <PrefRow label="Show viz overlay">
              <SegmentedControl
                options={[
                  { value: true, label: 'On' },
                  { value: false, label: 'Off' },
                ]}
                value={showVizImage}
                onChange={setShowVizImage}
              />
            </PrefRow>
            <PrefRow label="Show crop mask">
              <SegmentedControl
                options={[
                  { value: true, label: 'On' },
                  { value: false, label: 'Off' },
                ]}
                value={showCropMask}
                onChange={setShowCropMask}
              />
            </PrefRow>
            <PrefRow label="Hide spectators">
              <SegmentedControl
                options={[
                  { value: true, label: 'On' },
                  { value: false, label: 'Off' },
                ]}
                value={hideSpectators}
                onChange={setHideSpectators}
              />
            </PrefRow>
            <PrefRow label="Auto-remove out-of-crop faces">
              <SegmentedControl
                options={[
                  { value: 'off', label: 'Off' },
                  { value: 'strict', label: 'Strict' },
                  { value: 'normal', label: 'Normal' },
                  { value: 'relaxed', label: 'Relaxed' },
                ]}
                value={autoRemoveMode}
                onChange={handleAutoRemoveModeChange}
              />
            </PrefRow>
            <p className={styles.hint}>
              When a crop is saved, tagged faces whose bounding box falls outside the crop
              get their person assignment removed automatically. Strict = 100% inside,
              Normal = ≥ 90%, Relaxed = ≥ 75%.
            </p>
          </div>
        </Card>

        {user.permissions && Object.keys(user.permissions).length > 0 && (
          <Card>
            <Eyebrow className={styles.sectionEyebrow}>{t('me.permissions')}</Eyebrow>
            <div className={styles.permissions}>
              {Object.entries(user.permissions).map(([key, value]) => (
                <div
                  key={key}
                  className={`${styles.permission} ${
                    value ? styles.permissionGranted : styles.permissionDenied
                  }`}
                >
                  <span className={styles.permissionIcon}>{value ? '+' : '−'}</span>
                  <span className={styles.permissionName}>{key.replace(/_/g, ' ')}</span>
                </div>
              ))}
            </div>
          </Card>
        )}

        {user.camera_serials?.length > 0 && (
          <Card>
            <Eyebrow className={styles.sectionEyebrow}>{t('me.cameras')}</Eyebrow>
            <div className={styles.cameras}>
              {user.camera_serials.map((serial) => (
                <code key={serial} className={styles.cameraSerial}>
                  {serial}
                </code>
              ))}
            </div>
          </Card>
        )}

        <Card>
          <Eyebrow className={styles.sectionEyebrow}>Sessions</Eyebrow>
          {sessionsError && (
            <p className={styles.hint}>Could not load sessions: {sessionsError}</p>
          )}
          {!sessionsError && sessions.length === 0 && (
            <p className={styles.hint}>No active sessions.</p>
          )}
          {sessions.length > 0 && (
            <ul className={styles.sessionsList}>
              {sessions.map((s) => {
                const where = [s.city, s.country_name].filter(Boolean).join(', ');
                const last = s.last_activity ? new Date(s.last_activity).toLocaleString() : 'never';
                return (
                  <li key={s.id} className={styles.sessionRow}>
                    <span className={styles.sessionType}>{s.session_type}</span>
                    <span className={styles.sessionWhere}>
                      {where || s.ip_address || 'unknown'}
                    </span>
                    <Mono className={styles.sessionLast}>{last}</Mono>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        <div className={styles.metaSection}>
          <div className={styles.fieldGrid}>
            <Field label={t('me.userId')}>
              <Mono className={styles.fieldValueMono}>{user.id}</Mono>
            </Field>
            {user.is_service_account && (
              <Field label={t('me.accountType')}>
                <span className={styles.serviceBadge}>{t('me.serviceAccount')}</span>
              </Field>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
